/**
 * SiteGuard — CMS-driven site-wide maintenance switch.
 *
 * When `brand.site_online === false` in CMS, ANY route that isn't the
 * hidden staff-login URL is replaced with a Maintenance page. This lets an
 * admin take the entire public site (landing / /login / /register / /portal)
 * offline in one click while still being able to sign in via
 * `/ops-console-fx-9k2m4` to flip it back.
 *
 * Rendered as a wrapper around <Outlet /> so it can compose with existing
 * routes without duplicating each Route element.
 */
import { Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";

// Only these paths remain reachable while the site is offline — everything else
// gets the maintenance page. Keep this list narrow.
const ALLOWLIST_PREFIXES = ["/ops-console-fx-9k2m4"];

function MaintenancePage({ brand }) {
  const message = brand?.maintenance_message ||
    "We're upgrading systems and will be back online shortly. Please try again in a few minutes.";
  const supportEmail = brand?.support_email;
  const brandName = brand?.name || "FuelExpress";

  return (
    <div className="grid min-h-screen place-items-center bg-background p-6 text-foreground" data-testid="maintenance-page">
      <div className="w-full max-w-lg space-y-6 rounded-2xl border border-border/60 bg-card p-10 text-center shadow-lg">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300">
          {/* wrench icon */}
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{brandName}</div>
          <h1 className="font-display text-2xl font-black tracking-tight sm:text-3xl">Down for maintenance</h1>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
        {supportEmail && (
          <div className="text-xs text-muted-foreground">
            Urgent? Email <a href={`mailto:${supportEmail}`} className="text-primary underline">{supportEmail}</a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SiteGuard() {
  const { pathname } = useLocation();
  const { data, isLoading } = useQuery({
    queryKey: ["cms-landing"],   // Same key as ThemeInjector — shared cache, one HTTP call
    queryFn: () => api.get("/cms/landing").then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });

  // While CMS is loading, render children rather than block; we don't want
  // a maintenance flash on cold load when the site is actually online.
  if (isLoading || !data?.content) return <Outlet />;

  const siteOnline = data.content.brand?.site_online !== false;
  if (siteOnline) return <Outlet />;

  // Site offline — check allowlist first
  if (ALLOWLIST_PREFIXES.some((p) => pathname.startsWith(p))) return <Outlet />;

  return <MaintenancePage brand={data.content.brand} />;
}
